import React from 'react'
import {
  TestSessionDetailWithResult_testSession_PretestSessionSurveys,
  Symptom,
  useSymptomsForm,
  PreviousExposureFieldLabels,
  PreviousExposureFieldName,
  useAddPreTestSurveyResponses,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { symptomsDefaultSorter } from 'utils/helpers'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { Dialog } from 'components/Dialog'
import Card from 'components/Card'
import Button from 'components/Button'
import { Checkbox } from 'components/Checkbox'
import { CheckboxGroup } from 'components/CheckboxGroup'
import { DetailsList } from 'components/DetailsList'
import { Text } from 'components/Typography'
import { Box } from 'components/Layout'

export interface PretestSurveyProps {
  userId: number | null
  testSessionId: string
  PretestSessionSurvey?: TestSessionDetailWithResult_testSession_PretestSessionSurveys
  isEditMode?: boolean
}

const PretestSurvey = ({
  userId,
  PretestSessionSurvey,
  testSessionId,
  isEditMode = false,
}: PretestSurveyProps) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [addPretestSurveyResponses] = useAddPreTestSurveyResponses()

  const symptomsForm = useSymptomsForm({
    onSubmit: async (values, formikHelpers) => {
      try {
        await addPretestSurveyResponses({
          variables: {
            inputData: {
              userId: String(userId),
              testSessionId,
              PretestSessionSurveyResponses: values.symptoms.map((symptom) => ({
                key: 'symptom',
                value: symptom,
              })),
            },
          },
        })
        setIsDialogOpen(false)
        formikHelpers.resetForm()
      } catch (e) {
        // ignore error
      }
    },
  })
  const errorMessage = Array.isArray(symptomsForm.errors.symptoms)
    ? symptomsForm.errors.symptoms.join(' ')
    : symptomsForm.errors.symptoms
  return (
    <Box display="grid" gridGap="0.25rem" justifyContent="start">
      {PretestSessionSurvey ? (
        <>
          <Text fontSize="l">Pretest survey</Text>
          <DetailsList
            list={PretestSessionSurvey.PretestSessionSurveyResponses.filter(
              ({ key }) => key === 'previousExposure'
            ).map(({ value }) => ({
              label: '*',
              value: PreviousExposureFieldLabels[value as PreviousExposureFieldName],
            }))}
          />
          <Text fontSize="s">Symptoms</Text>
          <DetailsList
            list={PretestSessionSurvey.PretestSessionSurveyResponses.filter(
              ({ key }) => key === 'symptom'
            ).map(({ value }) => ({ label: '*', value }))}
          />
        </>
      ) : (
        isEditMode && (
          <Button onClick={() => setIsDialogOpen(true)} appearance="link">
            Fill pre-test survey
          </Button>
        )
      )}
      <Dialog
        variant={isDesktop ? 'right' : 'fullscreen'}
        maxWidth={isDesktop ? '500px' : '100%'}
        isOpen={isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
      >
        <Card.Title onDismiss={() => setIsDialogOpen(false)} />
        <Card.Content>
          <CheckboxGroup
            label="Symptoms"
            value={symptomsForm.values.symptoms}
            onChange={(value) => {
              void symptomsForm.setFieldValue('symptoms', value)
            }}
            errorMessage={errorMessage}
          >
            {Object.values(Symptom)
              .sort(symptomsDefaultSorter)
              .map((symptom) => (
                <Checkbox key={symptom} label={symptom} name={symptom} />
              ))}
          </CheckboxGroup>
        </Card.Content>
        <Box mt="1rem" display="flex" justifyContent="center">
          <Button
            onClick={() => {
              symptomsForm.handleSubmit()
            }}
          >
            Submit
          </Button>
        </Box>
      </Dialog>
    </Box>
  )
}

export { PretestSurvey }
